import KoobPlaceholder from '../../components/KoobPlaceholder';
import { useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { formatDate } from '@koob/dates';
import {
  KoobBadge,
  KoobInputCountries,
  KoobInputDate,
  KoobInputSelect,
  KoobInputStepper,
  KoobInputText,
  KoobInputTextarea,
  KoobLocationSearch,
  KoobPill,
  KoobPillGroup,
  KoobSelectableCard,
  KoobTooltip,
  KoobButton,
} from '../../components';
import { computeEndDate } from './buildTripPayload';
import { GroupSizeField, ChildrenAgesField, RoomsSplitField } from '../pax';
import type {
  CityValue,
  CountryOption,
  DmcOption,
  TripTagOption,
  TripWizardConfig,
  WizardValues,
} from './types';

export interface FieldProps {
  values: WizardValues;
  set: <K extends keyof WizardValues>(key: K, value: WizardValues[K]) => void;
  patch: (partial: Partial<WizardValues>) => void;
  config: TripWizardConfig;
}

export interface StepDef {
  id: string;
  required: boolean;
  titleKey: string;
  descKey?: string;
  // Rendered as <step.Field …/>: React 17 only accepts an element or null there,
  // which ReactNode is too wide for.
  Field: (props: FieldProps) => ReactElement | null;
  validate: (values: WizardValues) => boolean;
}

// Fallback pills when a host passes no real options (e.g. the UI showcase, or a
// getThemes/getStyles outage). Id === label so a selection is still coherent,
// just not id-backed — real hosts pass API-backed TripTagOptions.
const asFallbackOptions = (labels: string[]): TripTagOption[] =>
  labels.map((label) => ({ id: label, label }));
const DEFAULT_THEMES = asFallbackOptions([
  'Cruises',
  'History & heritage',
  'Beach stay',
  'Romantic',
  'Eco friendly',
  'Food & Wine',
  'Accessible travel',
  'Wellness',
  'Golf',
  'Cycling',
  'Hiking & Trekking',
  'Family',
]);
const DEFAULT_HOTEL_STYLES = asFallbackOptions([
  'Villa',
  'Well-being',
  'Inn',
  'Holiday cottage',
  'Camping',
  'Urban',
  'Resort',
  'Luxury',
  'Charming · Boutique',
  'Serviced apartments',
  'Atypical',
  'Design',
]);

function fmtDate(d: Date): string {
  return formatDate(d, 'D MMM YYYY');
}

function SubLabel({ children }: { children: ReactNode }) {
  return (
    <span className="mb-1 block text-xs uppercase tracking-wide text-gray-400">
      {children}
    </span>
  );
}

// ── 01 name ──────────────────────────────────────────────────────────────
// The hosts load flag-icon-css globally; alpha2 comes from the locales API.
function LocaleLabel({
  option,
}: {
  option: { label: string; alpha2?: string };
}) {
  return (
    <span className="flex items-center gap-1.5">
      {option.alpha2 && (
        <span
          className={`flag-icon flag-icon-${option.alpha2} h-4 w-5 shrink-0 rounded-sm`}
          aria-hidden
        />
      )}
      {option.label}
    </span>
  );
}

function NameField({ values, set, config }: FieldProps) {
  const renderLocale = (option: { label: string; alpha2?: string }) => (
    <LocaleLabel option={option} />
  );
  return (
    <div className="flex flex-col gap-4">
      <KoobInputText
        value={values.name}
        onChange={(e) => set('name', e.target.value)}
        placeholder={config.t('name.placeholder')}
        autoFocus
      />
      {!!config.localeOptions?.length && (
        <div>
          <SubLabel>{config.t('name.locale')}</SubLabel>
          <div className="w-48">
            <KoobInputSelect
              options={config.localeOptions}
              value={values.locale ?? config.locale}
              onValueChange={(v: string) => set('locale', v)}
              renderOption={renderLocale}
              renderValue={renderLocale}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── 02 countries ─────────────────────────────────────────────────────────
function CountriesField({ values, set, config }: FieldProps) {
  const [options, setOptions] = useState<CountryOption[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    setLoading(true);
    config
      .loadCountries()
      .then((o) => live && (setOptions(o), setLoading(false)))
      .catch(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // Once per mount: `config` identity churns with host re-renders (inline
    // onDone/onExit props), and each re-run discarded the in-flight fetch —
    // an endless spinner. The country list is static for the wizard's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byId = useMemo(
    () => Object.fromEntries(options.map((o) => [String(o.value), o])),
    [options],
  );
  const value = values.countries.map((c) => c.value);
  const count = values.countries.length;

  return (
    <>
      <KoobInputSelect
        multiple
        searchable
        inline
        loading={loading}
        options={options.map((o) => ({ value: o.value, label: o.label }))}
        value={value}
        onValueChange={(ids: (string | number)[]) =>
          set(
            'countries',
            ids
              .map((id) => byId[String(id)])
              .filter(Boolean) as CountryOption[],
          )
        }
        placeholder={config.t('countries.placeholder')}
      />
      {count > 0 && (
        <p className="mt-2 text-sm text-gray-400">
          {config.t('countries.selected', { count })}
        </p>
      )}
    </>
  );
}

// ── 03 dmc (TO only) ─────────────────────────────────────────────────────
function DmcCardTitle({
  option,
  config,
}: {
  option: DmcOption;
  config: TripWizardConfig;
}) {
  if (!option.partners?.length) return <>{option.label}</>;
  return (
    <span className="inline-flex items-center gap-2">
      {option.label}
      <KoobTooltip side="bottom" tip={option.partners.join(', ')}>
        <KoobBadge color="orange" size="sm">
          {config.t('dmc.partnerships', { count: option.partners.length })}
        </KoobBadge>
      </KoobTooltip>
    </span>
  );
}

function DmcField({ values, patch, config }: FieldProps) {
  const [options, setOptions] = useState<DmcOption[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (config.dmc.mode !== 'select') return;
    let live = true;
    setLoading(true);
    config.dmc
      .loadOptions(values.countries)
      .then((o) => live && (setOptions(o), setLoading(false)))
      .catch(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // Refetch only when the chosen countries change — not on config identity
    // churn (see CountriesField above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.countries]);

  if (loading)
    return (
      <div className="flex flex-col gap-3">
        <KoobPlaceholder className="h-16 rounded-lg" count={3} />
      </div>
    );
  return (
    // data-testid consumed by the TO trip-designer e2e specs.
    <div className="flex flex-col gap-3" data-testid="select-dmc-organization">
      {options.map((o) => (
        <KoobSelectableCard
          key={o.value}
          title={<DmcCardTitle option={o} config={config} />}
          selected={values.dmcId === o.value}
          onClick={() => patch({ dmcId: o.value, dmcLabel: o.label })}
        />
      ))}
    </div>
  );
}

// ── 04 start / end city (optional) ───────────────────────────────────────
const MIN_CITY_SEARCH = 2;

function CitySelect({
  city,
  onPick,
  config,
}: {
  city?: CityValue;
  onPick: (city?: CityValue) => void;
  config: TripWizardConfig;
}) {
  const noop = useMemo(() => () => Promise.resolve([] as CityValue[]), []);
  return (
    <KoobLocationSearch
      value={city ?? null}
      onValueChange={(c) => onPick(c ?? undefined)}
      fetchResults={config.loadCities ?? noop}
      minLength={MIN_CITY_SEARCH}
      placeholder={config.t('location.city')}
      hint={config.t('location.searchHint', { min: MIN_CITY_SEARCH })}
      emptyState={config.t('location.noResults')}
    />
  );
}

function StartEndField({ values, set, config }: FieldProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <SubLabel>{config.t('location.start')}</SubLabel>
        <CitySelect
          city={values.startLocation}
          config={config}
          onPick={(c) => set('startLocation', c)}
        />
      </div>
      <div>
        <SubLabel>{config.t('location.end')}</SubLabel>
        <CitySelect
          city={values.endLocation}
          config={config}
          onPick={(c) => set('endLocation', c)}
        />
      </div>
    </div>
  );
}

// ── 05 dates + duration ──────────────────────────────────────────────────
function DatesField({ values, set, config }: FieldProps) {
  const minDate = useMemo(() => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    return d;
  }, []);
  const end = computeEndDate(values.departureDate, values.duration);
  return (
    <div>
      <div className="flex flex-wrap items-start gap-8">
        <div>
          <SubLabel>{config.t('dates.departure')}</SubLabel>
          <KoobInputDate
            value={values.departureDate}
            onChange={(d) => set('departureDate', d)}
            minDate={minDate}
            placeholder={config.t('dates.chooseDate')}
          />
        </div>
        <div>
          <SubLabel>{config.t('dates.duration')}</SubLabel>
          <div className="flex items-center gap-3">
            <KoobInputStepper
              value={values.duration}
              onValueChange={(n) => set('duration', n)}
              min={1}
            />
            <span className="text-sm text-gray-400">
              {config.t('dates.days')}
            </span>
          </div>
        </div>
      </div>
      {values.departureDate && end && (
        <p className="mt-4 text-sm text-gray-400">
          {config.t('dates.range', {
            start: fmtDate(values.departureDate),
            end: fmtDate(end),
          })}
        </p>
      )}
    </div>
  );
}

// ── 06 travellers (adults + children + birthdates) ───────────────────────
function TravellersField({ values, patch, config }: FieldProps) {
  const childCount = values.childrenBirthdates.length;

  const setAdults = (n: number) => {
    const firstRoom = values.rooms[0] ?? { adults: 0, children: 0 };
    const otherRooms = values.rooms.slice(1);
    const otherAdults = otherRooms.reduce((s, r) => s + r.adults, 0);
    const firstRoomAdults = Math.max(0, n - otherAdults);
    patch({
      adultPax: n,
      rooms: [{ ...firstRoom, adults: firstRoomAdults }, ...otherRooms],
    });
  };

  const setChildCount = (n: number) => {
    const birthdates = Array.from(
      { length: n },
      (_, i) => values.childrenBirthdates[i] ?? null,
    );
    const firstRoom = values.rooms[0] ?? { adults: 0, children: 0 };
    const otherRooms = values.rooms.slice(1);
    const otherChildren = otherRooms.reduce((s, r) => s + r.children, 0);
    const firstRoomChildren = Math.max(0, n - otherChildren);
    patch({
      childrenBirthdates: birthdates,
      rooms: [
        {
          ...firstRoom,
          children: firstRoomChildren,
          childrenBirthdates: birthdates,
        },
        ...otherRooms,
      ],
    });
  };

  const setBirthdate = (i: number, d: Date | null) => {
    const next = [...values.childrenBirthdates];
    next[i] = d;
    patch({
      childrenBirthdates: next,
      rooms: values.rooms.map((r, idx) =>
        idx === 0 ? { ...r, childrenBirthdates: next } : r,
      ),
    });
  };

  return (
    <div>
      <GroupSizeField
        adults={values.adultPax}
        childrenCount={childCount}
        onAdultsChange={setAdults}
        onChildrenCountChange={setChildCount}
        t={config.t}
      />
      <ChildrenAgesField
        birthdates={values.childrenBirthdates}
        onBirthdateChange={setBirthdate}
        t={config.t}
      />
    </div>
  );
}

// ── 08 rooms split ───────────────────────────────────────────────────────
function RoomsField({ values, set, config }: FieldProps) {
  return (
    <RoomsSplitField
      adultsTotal={values.adultPax}
      childrenTotal={values.childrenBirthdates.length}
      rooms={values.rooms}
      onRoomsChange={(rooms) => set('rooms', rooms)}
      t={config.t}
    />
  );
}

// ── 09 looking for (themes + hotel styles) ───────────────────────────────
function PillPicker({
  title,
  options,
  selected,
  onToggle,
  max,
}: {
  title: string;
  options: TripTagOption[];
  selected: TripTagOption[];
  onToggle: (o: TripTagOption) => void;
  max: number;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <SubLabel>{title}</SubLabel>
        <span className="text-xs text-gray-400">
          {selected.length}/{max}
        </span>
      </div>
      <KoobPillGroup>
        {options.map((o) => {
          const active = selected.some((s) => s.id === o.id);
          return (
            <KoobPill
              key={o.id}
              active={active}
              disabled={!active && selected.length >= max}
              onClick={() => onToggle(o)}
            >
              {o.label}
            </KoobPill>
          );
        })}
      </KoobPillGroup>
    </div>
  );
}

function LookingForField({ values, set, config }: FieldProps) {
  const toggle = (
    key: 'themes' | 'hotelStyles',
    o: TripTagOption,
    max: number,
  ) => {
    const cur = values[key];
    if (cur.some((x) => x.id === o.id))
      set(
        key,
        cur.filter((x) => x.id !== o.id),
      );
    else if (cur.length < max) set(key, [...cur, o]);
  };
  return (
    <div className="flex flex-col gap-6">
      <PillPicker
        title={config.t('lookingFor.themes')}
        options={config.themeOptions ?? DEFAULT_THEMES}
        selected={values.themes}
        onToggle={(o) => toggle('themes', o, 3)}
        max={3}
      />
      <PillPicker
        title={config.t('lookingFor.hotelStyles')}
        options={config.hotelStyleOptions ?? DEFAULT_HOTEL_STYLES}
        selected={values.hotelStyles}
        onToggle={(o) => toggle('hotelStyles', o, 3)}
        max={3}
      />
    </div>
  );
}

// ── 10 brief ─────────────────────────────────────────────────────────────
function BriefField({ values, set, config }: FieldProps) {
  const readOnly = !!config.briefReadOnly;
  return (
    <KoobInputTextarea
      value={values.brief}
      onChange={(e) => set('brief', e.target.value)}
      minRows={4}
      placeholder={config.t('brief.placeholder')}
      readOnly={readOnly}
    />
  );
}

/** Assemble the active step list for the given host config. */
export function buildSteps(
  config: TripWizardConfig,
  opts?: {
    mode?: 'create' | 'edit';
    hasBrief?: boolean;
    /** Solo traveller (1 adult, no children): no room split to ask about. */
    skipRooms?: boolean;
    /** Steps left out of the flow, their answer being fixed by the host. */
    skipStepIds?: string[];
  },
): StepDef[] {
  const steps: StepDef[] = [
    {
      id: 'name',
      required: true,
      titleKey: 'name.title',
      descKey: 'name.description',
      Field: NameField,
      validate: (v) => v.name.trim().length > 0,
    },
    {
      id: 'countries',
      required: true,
      titleKey: 'countries.title',
      descKey: 'countries.description',
      Field: CountriesField,
      validate: (v) => v.countries.length > 0,
    },
  ];

  if (config.dmc.mode === 'select') {
    steps.push({
      id: 'dmc',
      required: true,
      titleKey: 'dmc.title',
      descKey: 'dmc.description',
      Field: DmcField,
      validate: (v) => !!v.dmcId,
    });
  }

  if (config.loadCities) {
    steps.push({
      id: 'location',
      required: false,
      titleKey: 'location.title',
      descKey: 'location.description',
      Field: StartEndField,
      validate: () => true,
    });
  }

  steps.push(
    {
      id: 'dates',
      required: true,
      titleKey: 'dates.title',
      descKey: 'dates.description',
      Field: DatesField,
      validate: (v) => !!v.departureDate && v.duration >= 1,
    },
    {
      id: 'travellers',
      required: true,
      titleKey: 'travellers.title',
      descKey: 'travellers.description',
      Field: TravellersField,
      validate: (v) =>
        v.adultPax >= 1 && v.childrenBirthdates.every((b) => b != null),
    },
  );

  if (!opts?.skipRooms) {
    steps.push({
      id: 'rooms',
      required: true,
      titleKey: 'rooms.title',
      descKey: 'rooms.description',
      Field: RoomsField,
      validate: (v) => {
        const a = v.rooms.reduce((s, r) => s + r.adults, 0);
        const c = v.rooms.reduce((s, r) => s + r.children, 0);
        return (
          v.rooms.length > 0 &&
          v.rooms.every((r) => r.adults >= 1) &&
          a === v.adultPax &&
          c === v.childrenBirthdates.length
        );
      },
    });
  }

  steps.push({
    id: 'lookingFor',
    required: false,
    titleKey: 'lookingFor.title',
    descKey: 'lookingFor.description',
    Field: LookingForField,
    validate: () => true,
  });

  // The DMC is never asked for a brief (read-only host): skip it in the create
  // flow and only surface it when editing a trip that already carries one.
  const includeBrief = config.briefReadOnly
    ? opts?.mode === 'edit' && !!opts?.hasBrief
    : true;
  if (includeBrief) {
    steps.push({
      id: 'brief',
      required: false,
      titleKey: 'brief.title',
      descKey: 'brief.description',
      Field: BriefField,
      validate: () => true,
    });
  }

  return opts?.skipStepIds?.length
    ? steps.filter((step) => !opts.skipStepIds!.includes(step.id))
    : steps;
}

/**
 * The steps among `stepIds` the values already answer, i.e. that would pass
 * their own validation. A host-fixed step is only skipped when answered, so the
 * flow never saves a trip missing it.
 */
export function answeredStepIds(
  config: TripWizardConfig,
  stepIds: string[],
  values: WizardValues,
): string[] {
  const steps = buildSteps(config);
  return stepIds.filter(
    (id) => steps.find((step) => step.id === id)?.validate(values) ?? false,
  );
}
